<script setup lang="ts">
import { ref, computed, watch, onUnmounted, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { useForm, useFieldArray } from 'vee-validate';
import * as yup from 'yup';
import { routePlansApi, type RoutePlan, type RoutePlanForm } from '@/api/route-plans';
import { groupsApi } from '@/api/groups';
import { routePlanExpectedVisitsApi } from '@/api/planning/route-plan-expected-visits';
import { caregiversApi, type Caregiver } from '@/api/caregivers';
import { patientsApi, type PatientAddress } from '@/api/patients';
import { skillsApi } from '@/api/skills';
import type { SerializedUser } from '@/api/users';
import { careers } from '@/utils/careers';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useGroupFilterVisible } from '@/composables/useGroupFilterVisible';
import { useTableFilter } from '@/composables/useTableFilter';
import { usePermissions } from '@/composables/usePermissions';
import {
  buildVisitRow, visitRowFromPlanVisit, visitRowFromExpectedVisit, newExpectedVisits,
  buildRoutePlanFormData, caregiverHoursValid, windowOverrides, windowFitsDuration,
  pinnedGroupId, primaryOrFirstAddressId,
  addressHasCoordinates as addressInListHasCoordinates,
  DEFAULT_LUNCH_DURATION_MINUTES, LUNCH_DURATION_OPTIONS,
  type VisitRow, type SetupFormValues, type CaregiverHoursMap, type VisitWindow,
} from './route-plan-setup.utils';
import RoutePlanAddressDialog from './route-plan-address-dialog.vue';
import RoutePlanCoverageDialog from './route-plan-coverage-dialog.vue';
import RoutePlanOptimizeOverlay from './route-plan-optimize-overlay.vue';
import RoutePlanPatientPicker from './route-plan-patient-picker.vue';
import RoutePlanExpectedVisitsPanel from './route-plan-expected-visits-panel.vue';

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { t } = useI18n();
const { accessibleCareers } = useAccessibleCareers();
const { hasPermission } = usePermissions();
const nameFilter = useTableFilter(['fullName']);

// Gate the whole flow (banner + action) on update permission; the saves would otherwise 403.
const canEditCaregivers = computed(() => hasPermission.value('caregivers', 'update'));
const canEditPatients = computed(() => hasPermission.value('patients', 'update'));

interface Props {
  routePlan: RoutePlan;
}

const props = defineProps<Props>();
const emit = defineEmits<{ optimized: [] }>();

// --- Schema ---

const groupsEnabled = computed(() => !!currentUser?.value?.companyGroupsEnabled);

// Same rule as group-filter-select: with a single visible group there is nothing
// to choose, so the group field and summary stay hidden.
const { shouldShow: groupChoiceVisible } = useGroupFilterVisible();

// Per-plan fixed-start tolerance is entered in minutes; cap at 12h (matches the
// backend RoutePlan validation of 12.hours seconds).
const MAX_FIXED_START_OFFSET_MINUTES = 720;

// Default window for visit rows: the company's optimizer window (back-office configurable)
// instead of a hardcoded range. Evaluated once — the current user doesn't change mid-form.
const defaultVisitWindow: VisitWindow = {
  start: currentUser?.value?.companyOptimizerWindowStart || '08:00',
  end: currentUser?.value?.companyOptimizerWindowEnd || '20:00',
};

const validationSchema = yup.object({
  date: yup.string().required(t('routePlan.validation.dateRequired')),
  career: yup.string().required(t('routePlan.validation.careerRequired')),
  groupId: groupsEnabled.value ?
    yup.number().nullable().required(t('routePlan.validation.groupRequired')) :
    yup.number().nullable(),
  caregiverIds: yup.array().of(yup.number())
    .min(1, t('routePlan.validation.atLeastOneCaregiver')),
  visits: yup.array()
    .min(1, t('routePlan.validation.atLeastOneVisit'))
    .of(yup.object({
      patientId: yup.number().required(t('routePlan.validation.patientRequired')),
      durationMinutes: yup.number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .typeError(t('routePlan.validation.durationRequired'))
        .required(t('routePlan.validation.durationRequired'))
        .min(1, t('routePlan.validation.durationPositive')),
      useFixedTime: yup.boolean(),
      fixedStartTime: yup.string().when('useFixedTime', {
        is: true,
        then: (schema) => schema.required(t('routePlan.validation.fixedTimeRequired')),
        otherwise: (schema) => schema.notRequired(),
      }),
      timeWindowStart: yup.string().when('useFixedTime', {
        is: (val: boolean) => !val,
        then: (schema) => schema.required(t('routePlan.validation.timeWindowRequired')),
        otherwise: (schema) => schema.notRequired(),
      }),
      timeWindowEnd: yup.string().when('useFixedTime', {
        is: (val: boolean) => !val,
        then: (schema) => schema.required(t('routePlan.validation.timeWindowRequired'))
          // "Hasta" is the deadline for the visit to END: the window must fit the duration.
          .test('window-fits-duration', t('routePlan.validation.windowTooShort'), function (value) {
            const parent = this.parent as { timeWindowStart?: string; durationMinutes?: number };

            return windowFitsDuration(parent.timeWindowStart ?? '', value ?? '',
              Number(parent.durationMinutes));
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    })),
  // Blank/null means "inherit the company default"; a present value must be a positive
  // number of minutes, capped at 12h (a wider window spans the whole planning day).
  fixedStartOffsetMinutes: yup.number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable()
    .positive(t('routePlan.validation.fixedStartOffsetPositive'))
    .max(MAX_FIXED_START_OFFSET_MINUTES, t('routePlan.validation.fixedStartOffsetMax')),
});

// --- Form ---

// Company default fixed-start tolerance, in minutes. Prefills the per-plan override
// field and is the baseline used to decide whether the entered value is an override.
// Mirrors RouteOptimization::VisitTimeWindow::DEFAULT_FIXED_START_OFFSET.
const SECONDS_PER_MINUTE = 60;
const DEFAULT_FIXED_START_OFFSET_SECONDS = 3600;
const defaultOffsetMinutes = computed(() => Math.round(
  (currentUser?.value?.companyOptimizerFixedStartOffset ?? DEFAULT_FIXED_START_OFFSET_SECONDS) / SECONDS_PER_MINUTE,
));
// A persisted override is always a positive integer (model validation), so a truthy
// check safely covers both null (inherit) and a never-expected undefined.
const persistedOffsetSeconds = props.routePlan.optimizerFixedStartOffset;
const initialOffsetMinutes = persistedOffsetSeconds ?
  Math.round(persistedOffsetSeconds / SECONDS_PER_MINUTE) :
  defaultOffsetMinutes.value;

const { handleSubmit, errors, meta, defineField, validateField, values, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    date: props.routePlan.date,
    career: props.routePlan.career ?? undefined,
    groupId: props.routePlan.groupId ?? null,
    caregiverIds: props.routePlan.routePlanCaregivers?.map(c => c.caregiverId) || [],
    visits: props.routePlan.routePlanVisits?.map(v => visitRowFromPlanVisit(v, defaultVisitWindow)) || [],
    fixedStartOffsetMinutes: initialOffsetMinutes,
  },
});

const [date, dateAttrs] = defineField('date');
const [fixedStartOffsetMinutes, fixedStartOffsetMinutesAttrs] = defineField('fixedStartOffsetMinutes');
const [career, careerAttrs] = defineField('career');
const [groupId, groupIdAttrs] = defineField('groupId');
const [caregiverIds] = defineField('caregiverIds');
const typedCaregiverIds = computed(() => caregiverIds.value as number[]);

const { fields: visitFields, push: pushVisit, remove: removeVisitAt } = useFieldArray<VisitRow>('visits');

// --- Data queries ---

const formGroupIds = computed(() => (groupId.value ? [groupId.value as number] : []));

const { data: availableGroups } = useQuery({
  queryKey: ['groups', 'forFilters'],
  queryFn: () => groupsApi.indexForFilters(),
  initialData: [],
  enabled: groupsEnabled,
});

// With the field hidden there is no way to fill a missing group by hand, so pin
// the plan to the user's only group (rule + branches tested in the utils spec).
watch(availableGroups, (groups) => {
  if (!groupsEnabled.value) return;
  const pin = pinnedGroupId(groups, groupId.value as number | null);
  if (pin) groupId.value = pin;
}, { immediate: true });

const { data: allCaregivers } = useQuery({
  queryKey: ['caregivers', formGroupIds],
  queryFn: () => caregiversApi.index(undefined, formGroupIds.value.length > 0 ?
    formGroupIds.value.map(id => `groups_id_in[]=${id}`).join('&') :
    undefined),
});

const { data: patients } = useQuery({
  queryKey: ['patientsActive', formGroupIds],
  queryFn: () => patientsApi.index('active', formGroupIds.value),
});

// --- Computed helpers ---

function summaryDate(value: unknown): string | null {
  if (!value) return null;

  return new Date(`${value}T00:00:00`).toLocaleDateString('es-CL', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function summaryCareer(value: unknown): string | null {
  return careers.find(c => c.value === value)?.title ?? null;
}

function summaryGroup(value: unknown): string | null {
  if (!groupChoiceVisible.value || !value) return null;

  return (availableGroups.value ?? []).find(g => g.id === value)?.name ?? null;
}

const setupSummary = computed(() => [
  summaryDate(date.value),
  summaryCareer(career.value),
  summaryGroup(groupId.value),
].filter(Boolean).join(' · '));

const filteredCaregivers = computed(() => {
  if (!allCaregivers.value || !career.value) return [];

  return allCaregivers.value.filter(
    (c: Caregiver) => c.career === String(career.value),
  );
});

// When career changes, clear caregiver selection
watch(career, () => {
  setFieldValue('caregiverIds', []);
});

const selectedCaregiversList = computed(() => {
  if (!allCaregivers.value) return [];

  return allCaregivers.value.filter(
    (c: Caregiver) => typedCaregiverIds.value.includes(c.id),
  );
});

// Blocked caregivers per patient, from the saved plan's visits. Patients added in the
// form before saving get filtered after the draft save; the backend validation is the
// final backstop either way.
const blockedCaregiverIdsByPatient = computed(() => {
  const map = new Map<number, number[]>();
  props.routePlan.routePlanVisits?.forEach(v => {
    if (v.patientId) map.set(v.patientId, v.blockedCaregiverIds || []);
  });

  return map;
});

function caregiverSubtitle(caregiver: { address?: string; latitude?: string; longitude?: string }) {
  if (caregiver.latitude && caregiver.longitude) return undefined;

  return t('routePlan.noCoordinates');
}

const caregiversWithoutCoordinates = computed(() => {
  if (!allCaregivers.value) return [];

  return typedCaregiverIds.value.filter(id => {
    const cg = allCaregivers.value!.find((c: Caregiver) => c.id === id);

    return cg && (!cg.latitude || !cg.longitude);
  });
});

// --- Quick add-address dialog ---

// On save the dialog invalidates the relevant query, so allCaregivers/patients refetch and the
// *WithoutCoordinates warnings clear on their own — no @saved handler needed here.
const addressDialog = ref<{
  open: boolean;
  entityType: 'caregiver' | 'patient';
  entityId: number;
  entityName: string;
}>({ open: false, entityType: 'caregiver', entityId: 0, entityName: '' });

function openAddressDialog(
  entityType: 'caregiver' | 'patient', entityId: number, entityName: string,
) {
  addressDialog.value = { open: true, entityType, entityId, entityName };
}

const flaggedCaregivers = computed(() =>
  caregiversWithoutCoordinates.value
    .map(id => allCaregivers.value?.find((c: Caregiver) => c.id === id))
    .filter((c): c is Caregiver => !!c));

// --- Caregiver availability (per plan/day) ---

// Company default window, used to prefill newly selected caregivers. Mirrors the
// backend's RoutePlanCaregiver#set_default_availability fallbacks and the DB default
// lunch duration.
const defaultWindow = computed(() => {
  const user = currentUser?.value;

  return {
    availableFrom: user?.companyOptimizerWindowStart || '08:00',
    availableTo: user?.companyOptimizerWindowEnd || '20:30',
    lunchDurationMinutes: DEFAULT_LUNCH_DURATION_MINUTES,
  };
});

const lunchBreakEnabled = computed(() => !!currentUser?.value?.companyOptimizerLunchBreakEnabled);

// Per-caregiver editable hours, keyed by caregiver id. Kept parallel to the
// `caregiverIds` form field (which selection, skill and validation logic depend on)
// so that logic stays untouched. Seeded from persisted rows; new selections start
// from the company default.
const caregiverHours = ref<CaregiverHoursMap>(
  Object.fromEntries(
    (props.routePlan.routePlanCaregivers || []).map(rpc => [
      rpc.caregiverId,
      {
        availableFrom: rpc.availableFrom,
        availableTo: rpc.availableTo,
        lunchDurationMinutes: rpc.lunchDurationMinutes ?? DEFAULT_LUNCH_DURATION_MINUTES,
      },
    ]),
  ),
);

// Seed hours for newly added caregivers and drop deselected ones so they never leak
// into the payload.
watch(typedCaregiverIds, (ids) => {
  const selected = new Set(ids);
  ids.forEach(id => {
    if (!caregiverHours.value[id]) caregiverHours.value[id] = { ...defaultWindow.value };
  });
  Object.keys(caregiverHours.value).forEach(key => {
    if (!selected.has(Number(key))) delete caregiverHours.value[Number(key)];
  });
});

function hoursRowValid(id: number): boolean {
  const hours = caregiverHours.value[id];

  return !hours || caregiverHoursValid(hours);
}

const hoursValid = computed(() => typedCaregiverIds.value.every(hoursRowValid));

// --- Skill coverage ---

// Subspecialty catalog for the plan's career, so each visit can pick (or clear) its
// required skill. A visit's own skill_id is authoritative for the optimizer, so this
// also lets the dispatcher override a requirement-derived skill or remove it entirely.
const { data: careerSkills } = useQuery({
  queryKey: ['skills', career],
  queryFn: () => skillsApi.index(typeof career.value === 'string' ? career.value : undefined),
  enabled: computed(() => !!career.value),
  initialData: [],
});

const skillOptions = computed(
  () => (careerSkills.value || []).map(s => ({ value: s.id, title: s.name })),
);

function onVisitSkillChange(entry: { value: VisitRow }, skillId: number | null) {
  entry.value.skillId = skillId;
  entry.value.skillName = skillOptions.value.find(s => s.value === skillId)?.title ?? null;
}

// Set of skills covered by the selected caregivers, recomputed once per selection
// change instead of re-filtering the list per visit row on every render.
const selectedSkillIds = computed(
  () => new Set(selectedCaregiversList.value.flatMap((c: Caregiver) => c.skillIds || [])),
);

// Candidate caregivers for a visit's preselect dropdown: must cover the visit's
// required skill (if any) AND must not be blocked for the patient.
function caregiversForVisit(skillId: number | null, patientId: number | undefined) {
  let candidates = selectedCaregiversList.value;
  if (skillId) candidates = candidates.filter((c: Caregiver) => c.skillIds?.includes(skillId));

  const blocked = patientId ? blockedCaregiverIdsByPatient.value.get(patientId) : undefined;
  if (blocked && blocked.length > 0) {
    candidates = candidates.filter((c: Caregiver) => !blocked.includes(c.id));
  }

  return candidates;
}

// Clear assignments that violate the visit's skill constraint (e.g., saved drafts
// whose requirement gained a skill the assigned caregiver lacks). Watches only the
// (skill, assignment) pairs and the selection — not unrelated row edits.
watch(
  [() => visitFields.value.map(e => [e.value.skillId, e.value.assignedCaregiverId]), selectedCaregiversList],
  () => {
    const byId = new Map(selectedCaregiversList.value.map(c => [c.id, c]));
    visitFields.value.forEach(entry => {
      const { skillId, assignedCaregiverId } = entry.value;
      if (!skillId || !assignedCaregiverId) return;
      if (!byId.get(assignedCaregiverId)?.skillIds?.includes(skillId)) entry.value.assignedCaregiverId = null;
    });
  },
  { immediate: true },
);

// Skilled visits not covered by any selected caregiver: VROOM will leave them
// unassigned, so we warn (non-blocking) before optimization.
const uncoveredSkilledVisits = computed(() =>
  visitFields.value
    .map(entry => entry.value)
    .filter(v => v.skillId && !selectedSkillIds.value.has(v.skillId)));

// --- Patient ↔ visits sync ---

const selectedPatientIds = ref<number[]>(
  [...new Set(props.routePlan.routePlanVisits?.map(v => v.patientId).filter(Boolean) || [])],
);

function patientAddresses(patientId: number | undefined) {
  if (!patientId) return [];

  return patients.value?.find(p => p.id === patientId)?.patientAddressesAttributes ?? [];
}

function defaultAddressId(patientId: number): number | null {
  return primaryOrFirstAddressId(patientAddresses(patientId));
}

function addressHasCoordinates(patientId: number | undefined, addressId: number | null): boolean {
  return addressInListHasCoordinates(patientAddresses(patientId), addressId);
}

// Uses the address model, not the patient's legacy root latitude/longitude, so the autocomplete
// chip agrees with the missing-address banner and the per-visit checks.
function patientHasCoordinates(patient: { patientAddressesAttributes?: PatientAddress[] }): boolean {
  return addressInListHasCoordinates(patient.patientAddressesAttributes ?? [], null);
}

// Flag patients whose chosen visit address lacks coordinates — the optimizer skips those.
const patientsWithoutCoordinates = computed(() => {
  if (!patients.value) return [];

  const flagged = new Set<number>();
  visitFields.value.forEach(entry => {
    const { patientId, patientAddressId } = entry.value;
    if (patientId && !addressHasCoordinates(patientId, patientAddressId)) {
      flagged.add(patientId);
    }
  });

  return [...flagged];
});

function getPatientName(patientId: number) {
  const patient = patients.value?.find((p: { id: number }) => p.id === patientId);

  return patient ? `${patient.firstName} ${patient.lastName}` : `${t('routePlan.patient')} ${patientId}`;
}

// The "Prioritaria" toggle is strictly category-scoped: only patients whose current
// category opted in can flag visits. Uncategorized patients never see it.
function prioritizationAvailable(patientId: number | undefined) {
  if (!patientId) return false;

  return patients.value?.find(p => p.id === patientId)
    ?.currentServiceAssignment?.categoryPrioritizationEnabled ?? false;
}

// A persisted flag whose category later opted out stays visible so the user can see and
// clear it (it is inert in the optimizer); once unchecked, the control disappears.
function prioritizedToggleVisible(entry: { value: VisitRow }) {
  return prioritizationAvailable(entry.value.patientId) || entry.value.prioritized;
}

// Only patients with no usable address at all: adding a primary fixes their gap. One flagged
// merely because a visit points elsewhere still has a usable primary, which we'd wrongly demote.
const flaggedPatients = computed(() =>
  patientsWithoutCoordinates.value
    .filter(id => {
      const patient = patients.value?.find(p => p.id === id);

      return patient ? !patientHasCoordinates(patient) : false;
    })
    .map(id => ({ id, name: getPatientName(id) })));

function visitsForPatient(patientId: number) {
  return visitFields.value
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.value.patientId === patientId);
}

function addVisitForPatient(patientId: number) {
  pushVisit(buildVisitRow(
    patientId, getPatientName(patientId), defaultAddressId(patientId),
    windowOverrides(defaultVisitWindow),
  ));
}

function addVisitIfNew(patientId: number) {
  const alreadyHasVisit = visitFields.value.some(entry => entry.value.patientId === patientId);
  if (alreadyHasVisit) return;

  addVisitForPatient(patientId);
}

function removeAllVisitsForPatient(patientId: number) {
  // Remove in reverse order to avoid index shifting
  const indices = visitFields.value
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.value.patientId === patientId)
    .map(({ index }) => index)
    .reverse();
  indices.forEach(i => removeVisitAt(i));
}

watch(selectedPatientIds, (newIds, oldIds) => {
  const prevIds = oldIds || [];
  const added = newIds.filter(id => !prevIds.includes(id));
  const removed = prevIds.filter(id => !newIds.includes(id));

  added.forEach(id => addVisitIfNew(id));
  removed.forEach(id => removeAllVisitsForPatient(id));
});

const patientsWithVisits = computed(() => {
  const ids = new Set(visitFields.value.map(entry => entry.value.patientId).filter(Boolean));

  return [...ids];
});

// --- Form data builder ---

function buildFormData(): Partial<RoutePlanForm> {
  return buildRoutePlanFormData(values as SetupFormValues, caregiverHours.value, defaultOffsetMinutes.value);
}

// --- Save draft ---

const DEBOUNCE_MS = 3000;
const lastSavedAt = ref<string | null>(null);
const isSaving = ref(false);
const saveError = ref<string | null>(null);
const isMounted = ref(true);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

type ApiErrorData = { response?: { data?: { errors?: string[]; errorMessage?: string } } };

// Drop Rails' "Route plan visits/caregivers" nested-attribute prefix for readability.
function cleanNestedPrefix(message: string): string {
  return message.replace(/^Route plan (visits|caregivers)\s+/i, '');
}

// Prefer the server's human-readable validation messages over the machine code
// ('validation_failed') so the failure is understandable instead of opaque.
function serverErrorMessage(error: unknown): string {
  const data = (error as ApiErrorData).response?.data ?? {};
  const messages = (data.errors ?? []).map(cleanNestedPrefix);

  return messages.length ? messages.join(' · ') : (data.errorMessage ?? t('routePlan.saveError'));
}

async function saveDraft() {
  isSaving.value = true;
  saveError.value = null;
  try {
    await routePlansApi.update(props.routePlan.id, buildFormData());
    if (isMounted.value) {
      lastSavedAt.value = new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    }
  } catch (error) {
    if (isMounted.value) saveError.value = serverErrorMessage(error);
  } finally {
    if (isMounted.value) isSaving.value = false;
  }
}

function scheduleDebouncedSave() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => saveDraft(), DEBOUNCE_MS);
}

onUnmounted(() => {
  isMounted.value = false;
  if (debounceTimer) clearTimeout(debounceTimer);
});

// --- Optimize ---

const optimizeError = ref<string | null>(null);

const { mutate: optimize, isPending: isOptimizing } = useMutation({
  mutationFn: async () => {
    optimizeError.value = null;
    if (debounceTimer) clearTimeout(debounceTimer);
    await routePlansApi.update(props.routePlan.id, buildFormData());

    return routePlansApi.optimize(props.routePlan.id);
  },
  onSuccess: () => {
    emit('optimized');
  },
  onError: (error: Error & {
    response?: { data?: { errors?: string[]; errorMessage?: string; message?: string } };
  }) => {
    optimizeError.value = serverErrorMessage(error);
    emit('optimized');
  },
});

const onOptimize = handleSubmit(() => {
  optimize();
});

// --- Stepper ---

const LAST_STEP = 2;

function detectInitialStep(): number {
  if (visitFields.value.length > 0) return LAST_STEP;
  if (typedCaregiverIds.value.length > 0) return LAST_STEP;

  return 0;
}

const currentStep = ref(detectInitialStep());

const steps = computed(() => [
  t('routePlan.stepDateAndCareer'),
  t('routePlan.stepCaregivers'),
  t('routePlan.stepVisits'),
]);

const isLastStep = computed(() => currentStep.value === steps.value.length - 1);

const canGoStep1 = computed(() => {
  if (!date.value || career.value === undefined) return false;
  if (groupsEnabled.value && !groupId.value) return false;
  return true;
});

const groupFieldLocked = computed(() =>
  visitFields.value.length > 0 || typedCaregiverIds.value.length > 0,
);
const canGoStep2 = computed(() =>
  typedCaregiverIds.value.length > 0 &&
  caregiversWithoutCoordinates.value.length === 0 &&
  hoursValid.value,
);

const canGoToNextStep = computed(() => {
  if (currentStep.value === 0) return canGoStep1.value;
  if (currentStep.value === 1) return canGoStep2.value;

  return true;
});

const canOptimize = computed(
  () => career.value !== undefined &&
    typedCaregiverIds.value.length > 0 &&
    hoursValid.value &&
    visitFields.value.some(entry => entry.value.patientId) &&
    patientsWithoutCoordinates.value.length === 0,
);

type FormField = 'date' | 'career' | 'groupId' | 'caregiverIds';

const STEP_FIELDS: FormField[][] = [
  groupsEnabled.value ? ['date', 'career', 'groupId'] : ['date', 'career'],
  ['caregiverIds'],
];

const stepValidated = ref<Set<number>>(new Set());

async function validateStepAndAdvance(next: () => void) {
  stepValidated.value.add(currentStep.value);
  const fields = STEP_FIELDS[currentStep.value];
  if (!fields) {
    next();

    return;
  }

  const results = await Promise.all(fields.map(f => validateField(f)));
  if (results.every(r => r.valid)) next();
}

// Auto-save on step transitions
watch(currentStep, (newStep, oldStep) => {
  if (newStep > oldStep) saveDraft();
});

// Debounced auto-save on step 2 changes (skip initial load)
const initialized = ref(false);

watch(() => values.visits, () => {
  if (!initialized.value) {
    initialized.value = true;

    return;
  }
  if (currentStep.value === LAST_STEP) scheduleDebouncedSave();
}, { deep: true });

const fixedVisitsCount = computed(() =>
  visitFields.value.filter(entry => entry.value.useFixedTime).length,
);

function visitError(index: number, field: string): string | undefined {
  return (errors.value as Record<string, string>)[`visits[${index}].${field}`];
}

// --- Care planning pre-fill ---

const canLoadExpected = computed(() => (
  !!currentUser?.value?.companyCarePlanningEnabled &&
  !!date.value && !!career.value
));

// Expected visits are fetched eagerly on the visits step so the hero/CTA can show real
// counts. The reactive key re-fetches when step 1 edits change date/career/group.
const expectedQueryEnabled = computed(
  () => currentStep.value === LAST_STEP && canLoadExpected.value,
);
const {
  data: expectedIndex, isFetching: isLoadingExpected, error: expectedError,
  refetch: refetchExpected,
} = useQuery({
  queryKey: ['routePlanExpectedVisits', date, career, formGroupIds],
  queryFn: () => routePlanExpectedVisitsApi.index(
    date.value as string, career.value as string, formGroupIds.value,
  ),
  enabled: expectedQueryEnabled,
  retry: 1,
});

const expectedVisits = computed(() => expectedIndex.value?.expectedVisits ?? []);
// Slots a patient schedule block fully covers: listed by the panel, never loaded.
const blockedExpected = computed(() => expectedIndex.value?.blockedExpectedVisits ?? []);

const currentVisitRows = computed(() => visitFields.value.map((entry) => entry.value));
const newExpected = computed(() => newExpectedVisits(
  expectedVisits.value, currentVisitRows.value, defaultVisitWindow,
));
const expectedCount = computed(() => expectedVisits.value.length);
const newExpectedCount = computed(() => newExpected.value.length);

// Day-coverage dialog, read-only here: requirements vs existing shifts, so the planner sees
// which are already fulfilled without leaving the visits step (importing stays in "Cargar
// esperadas").
const coverageOpen = ref(false);

// The coverage endpoint reconciles against the PERSISTED plan, and the step's autosave is
// debounced — flush it and wait before opening, or the dialog would report the pre-save
// state (e.g. everything "missing" right after "Cargar esperadas").
async function openCoverage() {
  if (debounceTimer) clearTimeout(debounceTimer);
  await saveDraft();
  coverageOpen.value = true;
}

function loadExpectedVisits() {
  const toImport = newExpected.value;
  // Push rows BEFORE syncing selectedPatientIds: addVisitIfNew skips patients that already
  // have a row, so the selection watcher can never add default rows — deterministically,
  // regardless of watcher flush timing.
  toImport.forEach((ev) => pushVisit(
    visitRowFromExpectedVisit(ev, defaultAddressId(ev.patientId), defaultVisitWindow),
  ));
  const ids = new Set<number>([...selectedPatientIds.value, ...toImport.map((ev) => ev.patientId)]);
  selectedPatientIds.value = [...ids];
}
</script>

<template>
  <v-card class="pa-4">
    <v-card-title class="d-flex flex-wrap align-center ga-2 mb-4">
      <span>{{ t('routePlan.setup') }}</span>
      <span
        v-if="setupSummary"
        class="text-body-2 text-medium-emphasis"
      >· {{ setupSummary }}</span>
    </v-card-title>

    <v-stepper v-model="currentStep">
      <template #default="{ prev, next }">
        <v-stepper-header>
          <template
            v-for="(step, index) in steps"
            :key="`${step}-step`"
          >
            <v-stepper-item
              :step="step"
              :value="index"
              icon="mdi-form-select"
              :title="steps[index]"
              :complete="index < currentStep"
              :color="index <= currentStep ? 'primary' : 'gray'"
            />
            <v-divider
              v-if="index < steps.length - 1"
              :key="`divider-${index}`"
            />
          </template>
        </v-stepper-header>

        <v-stepper-window>
          <!-- Step 1: Date & Career -->
          <v-stepper-window-item>
            <v-card
              flat
              class="pt-4"
            >
              <v-text-field
                v-model="date"
                v-bind="dateAttrs"
                type="date"
                variant="outlined"
                :label="t('routePlan.date')"
                :error-messages="errors.date"
                class="mb-4"
              />

              <v-select
                v-model="career"
                v-bind="careerAttrs"
                :items="accessibleCareers"
                :label="t('routePlan.selectCareer')"
                variant="outlined"
                :error-messages="errors.career"
              />

              <v-autocomplete
                v-if="groupChoiceVisible"
                v-model="groupId"
                v-bind="groupIdAttrs"
                :items="availableGroups || []"
                item-title="name"
                item-value="id"
                :label="t('routePlan.groups.label')"
                variant="outlined"
                :error-messages="errors.groupId"
                :disabled="groupFieldLocked"
                :hint="groupFieldLocked ? t('routePlan.groups.locked') : undefined"
                :persistent-hint="groupFieldLocked"
                class="mt-4"
              />
            </v-card>
          </v-stepper-window-item>

          <!-- Step 2: Caregivers -->
          <v-stepper-window-item>
            <v-card
              flat
              class="pt-4"
            >
              <v-autocomplete
                v-model="caregiverIds"
                :items="filteredCaregivers || []"
                item-title="fullName"
                item-value="id"
                :custom-filter="nameFilter"
                :label="t('routePlan.selectCaregivers')"
                variant="outlined"
                multiple
                chips
                closable-chips
                :disabled="!career"
                :error-messages="stepValidated.has(1) ? errors.caregiverIds : undefined"
                class="mb-2"
              >
                <template #chip="{ props: chipProps, item }">
                  <v-chip
                    v-bind="chipProps"
                    :prepend-icon="item.raw.latitude && item.raw.longitude ? 'mdi-map-check' : 'mdi-map-marker-off'"
                    :color="item.raw.latitude && item.raw.longitude ? undefined : 'warning'"
                  >
                    {{ item.raw.fullName }}
                  </v-chip>
                </template>
                <template #item="{ props: itemProps, item }">
                  <v-list-item
                    v-bind="itemProps"
                    :subtitle="caregiverSubtitle(item.raw)"
                  >
                    <template
                      v-if="!item.raw.latitude || !item.raw.longitude"
                      #append
                    >
                      <v-icon color="warning">
                        mdi-map-marker-off
                      </v-icon>
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <v-alert
                v-if="canEditCaregivers && flaggedCaregivers.length > 0"
                type="warning"
                variant="tonal"
                class="mb-4"
              >
                <p class="mb-2">
                  {{ t('routePlan.coordinatesWarning') }}
                </p>
                <div class="d-flex flex-column ga-2">
                  <div
                    v-for="cg in flaggedCaregivers"
                    :key="`flagged-cg-${cg.id}`"
                    class="d-flex align-center justify-space-between ga-2"
                  >
                    <span class="text-body-2 text-truncate">{{ cg.fullName }}</span>
                    <v-btn
                      size="small"
                      variant="tonal"
                      color="warning"
                      prepend-icon="mdi-map-marker-plus"
                      @click="openAddressDialog('caregiver', cg.id, cg.fullName)"
                    >
                      {{ t('routePlan.addAddress') }}
                    </v-btn>
                  </div>
                </div>
              </v-alert>

              <p
                v-if="filteredCaregivers.length > 0"
                class="text-body-2 text-medium-emphasis mb-3"
              >
                {{ t('routePlan.selectedCount', {
                  count: typedCaregiverIds.length,
                  total: filteredCaregivers.length,
                }) }}
              </p>

              <!-- Per-caregiver availability for this day. Editable, prefilled with the
                   company default; the optimizer uses these as each vehicle's time window. -->
              <div
                v-if="selectedCaregiversList.length > 0"
                class="mb-4"
              >
                <p class="text-subtitle-2 mb-1">
                  {{ t('routePlan.caregiverHours.title') }}
                </p>
                <p class="text-caption text-medium-emphasis mb-3">
                  {{ t('routePlan.caregiverHours.subtitle') }}
                </p>

                <div
                  v-for="cg in selectedCaregiversList"
                  :key="`hours-${cg.id}`"
                  class="d-flex align-center ga-1 mb-5"
                >
                  <!-- Fixed-width name block so every row's time inputs align vertically,
                       regardless of name length or the presence of the no-coordinates icon. -->
                  <div class="d-flex align-center ga-1 caregiver-hours-name">
                    <v-icon
                      v-if="!cg.latitude || !cg.longitude"
                      color="warning"
                      size="small"
                    >
                      mdi-map-marker-off
                    </v-icon>
                    <span class="text-body-2 text-truncate">{{ cg.fullName }}</span>
                  </div>
                  <template v-if="caregiverHours[cg.id]">
                    <v-text-field
                      v-model="caregiverHours[cg.id].availableFrom"
                      type="time"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :error="!hoursRowValid(cg.id)"
                      :label="t('routePlan.timeWindowStart')"
                      style="max-width: 130px;"
                    />
                    <span class="text-medium-emphasis">–</span>
                    <v-text-field
                      v-model="caregiverHours[cg.id].availableTo"
                      type="time"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :error="!hoursRowValid(cg.id)"
                      :label="t('routePlan.timeWindowEnd')"
                      style="max-width: 130px;"
                    />
                    <template v-if="lunchBreakEnabled">
                      <span class="text-caption text-medium-emphasis ml-2">
                        {{ t('routePlan.lunchDuration.label') }}
                      </span>
                      <v-btn-toggle
                        v-model="caregiverHours[cg.id].lunchDurationMinutes"
                        mandatory
                        density="compact"
                        color="primary"
                        variant="outlined"
                        divided
                      >
                        <v-btn
                          v-for="option in LUNCH_DURATION_OPTIONS"
                          :key="option"
                          :value="option"
                          size="small"
                        >
                          {{ t(`routePlan.lunchDuration.option${option}`) }}
                        </v-btn>
                      </v-btn-toggle>
                    </template>
                  </template>
                </div>

                <v-alert
                  v-if="!hoursValid"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  {{ t('routePlan.caregiverHours.invalid') }}
                </v-alert>
              </div>
            </v-card>
          </v-stepper-window-item>

          <!-- Step 3: Turnos -->
          <v-stepper-window-item>
            <v-card
              flat
              class="pt-4"
            >
              <route-plan-patient-picker
                v-model="selectedPatientIds"
                :patients="patients ?? []"
              />

              <v-alert
                v-if="canEditPatients && flaggedPatients.length > 0"
                type="warning"
                variant="tonal"
                class="mb-4"
              >
                <p class="mb-2">
                  {{ t('routePlan.patientsCoordinatesWarning') }}
                </p>
                <div class="d-flex flex-column ga-2">
                  <div
                    v-for="p in flaggedPatients"
                    :key="`flagged-patient-${p.id}`"
                    class="d-flex align-center justify-space-between ga-2"
                  >
                    <span class="text-body-2 text-truncate">{{ p.name }}</span>
                    <v-btn
                      size="small"
                      variant="tonal"
                      color="warning"
                      prepend-icon="mdi-map-marker-plus"
                      @click="openAddressDialog('patient', p.id, p.name)"
                    >
                      {{ t('routePlan.addAddress') }}
                    </v-btn>
                  </div>
                </div>
              </v-alert>

              <route-plan-expected-visits-panel
                v-if="canLoadExpected"
                :expected-count="expectedCount"
                :new-count="newExpectedCount"
                :blocked="blockedExpected"
                :loading="isLoadingExpected"
                :saving="isSaving"
                :error="!!expectedError"
                @load="loadExpectedVisits"
                @open-coverage="openCoverage"
                @retry="refetchExpected()"
              />

              <!-- Grouped by patient -->
              <div
                v-for="patientId in patientsWithVisits"
                :key="patientId"
                class="mb-4"
              >
                <div class="d-flex align-center gap-3 mb-1">
                  <span class="text-subtitle-1 font-weight-bold">
                    {{ getPatientName(patientId!) }}
                  </span>
                  <v-chip
                    v-if="visitsForPatient(patientId!).length > 1"
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    {{ visitsForPatient(patientId!).length }} {{ t('routePlan.attentions') }}
                  </v-chip>
                  <v-spacer />
                  <v-btn
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click="addVisitForPatient(patientId!)"
                  >
                    <v-icon
                      start
                      size="small"
                    >
                      mdi-plus
                    </v-icon>
                    {{ t('routePlan.addAnotherVisit') }}
                  </v-btn>
                </div>

                <!-- Visit rows for this patient -->
                <div
                  v-for="{ entry, index } in visitsForPatient(patientId!)"
                  :key="entry.key"
                  class="ml-10 mb-2 pa-3 rounded-lg bg-grey-lighten-5 d-flex flex-wrap align-center ga-3"
                >
                  <!-- Timing cluster: when first, then how long -->
                  <div class="d-flex align-center ga-3 flex-wrap">
                    <v-text-field
                      v-if="entry.value.useFixedTime"
                      v-model="entry.value.fixedStartTime"
                      type="time"
                      :label="t('routePlan.fixedStartTime')"
                      variant="outlined"
                      density="compact"
                      class="visit-time-input"
                      :error-messages="visitError(index, 'fixedStartTime')"
                      :hide-details="!visitError(index, 'fixedStartTime')"
                    />
                    <template v-else>
                      <v-text-field
                        v-model="entry.value.timeWindowStart"
                        type="time"
                        :label="t('routePlan.timeWindowStart')"
                        variant="outlined"
                        density="compact"
                        class="visit-time-input"
                        :error-messages="visitError(index, 'timeWindowStart')"
                        :hide-details="!visitError(index, 'timeWindowStart')"
                      />
                      <span class="text-body-2 text-medium-emphasis">—</span>
                      <v-text-field
                        v-model="entry.value.timeWindowEnd"
                        type="time"
                        :label="t('routePlan.timeWindowEnd')"
                        :title="t('routePlan.timeWindowEndHint')"
                        variant="outlined"
                        density="compact"
                        class="visit-time-input"
                        :error-messages="visitError(index, 'timeWindowEnd')"
                        :hide-details="!visitError(index, 'timeWindowEnd')"
                      />
                    </template>

                    <v-switch
                      v-model="entry.value.useFixedTime"
                      :label="t('routePlan.fixedTimeShort')"
                      density="compact"
                      hide-details
                      color="primary"
                      class="flex-grow-0 flex-shrink-0"
                      @update:model-value="(val: boolean | null) => {
                        if (val && !entry.value.fixedStartTime) {
                          entry.value.fixedStartTime = entry.value.timeWindowStart || '08:00';
                        }
                      }"
                    />

                    <v-tooltip
                      v-if="entry.value.useFixedTime"
                      location="top"
                    >
                      <template #activator="{ props: tooltipProps }">
                        <v-icon
                          size="small"
                          color="medium-emphasis"
                          class="flex-grow-0 flex-shrink-0"
                          v-bind="tooltipProps"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ t('routePlan.fixedStartTimeHint') }}</span>
                    </v-tooltip>

                    <v-text-field
                      v-model.number="entry.value.durationMinutes"
                      type="number"
                      variant="outlined"
                      density="compact"
                      :label="t('routePlan.durationMinutes')"
                      class="visit-duration"
                      :suffix="t('routePlan.minutes')"
                      :error-messages="visitError(index, 'durationMinutes')"
                      :hide-details="!visitError(index, 'durationMinutes')"
                    />
                  </div>

                  <!-- Assignment cluster: who serves it, where -->
                  <div class="d-flex align-center ga-3 flex-wrap flex-grow-1">
                    <v-autocomplete
                      v-model="entry.value.assignedCaregiverId"
                      :items="caregiversForVisit(entry.value.skillId, entry.value.patientId)"
                      item-title="fullName"
                      item-value="id"
                      :custom-filter="nameFilter"
                      :label="t('routePlan.assignedCaregiver')"
                      variant="outlined"
                      density="compact"
                      clearable
                      hide-details
                      class="visit-caregiver"
                    />

                    <v-select
                      v-if="skillOptions.length > 0"
                      :model-value="entry.value.skillId"
                      :items="skillOptions"
                      variant="outlined"
                      density="compact"
                      clearable
                      hide-details
                      :label="t('routePlan.requiredSkill')"
                      :placeholder="t('routePlan.noRequiredSkill')"
                      persistent-placeholder
                      prepend-inner-icon="mdi-certificate-outline"
                      class="visit-skill"
                      @update:model-value="(val: number | null) => onVisitSkillChange(entry, val)"
                    />

                    <template v-if="prioritizedToggleVisible(entry)">
                      <v-switch
                        v-model="entry.value.prioritized"
                        :label="t('routePlan.prioritizedShort')"
                        density="compact"
                        hide-details
                        color="primary"
                        class="flex-grow-0 flex-shrink-0"
                      />

                      <v-tooltip location="top">
                        <template #activator="{ props: tooltipProps }">
                          <v-icon
                            size="small"
                            color="medium-emphasis"
                            class="flex-grow-0 flex-shrink-0"
                            v-bind="tooltipProps"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>{{ t('routePlan.prioritizedHint') }}</span>
                      </v-tooltip>
                    </template>

                    <!-- Address + delete stay together so the X is always right of the address
                         input and never wraps onto a line by itself. -->
                    <div class="d-flex align-center ga-3 ml-auto">
                      <v-select
                        v-if="patientAddresses(patientId).length > 1"
                        v-model="entry.value.patientAddressId"
                        :items="patientAddresses(patientId)"
                        :item-title="(a: PatientAddress) => a.label || a.address"
                        item-value="id"
                        variant="outlined"
                        density="compact"
                        hide-details
                        :label="t('routePlan.visitAddress')"
                        class="visit-address"
                      />

                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="error"
                        class="flex-shrink-0"
                        @click="removeVisitAt(index)"
                      >
                        <v-icon size="small">
                          mdi-close
                        </v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>

              <v-alert
                v-if="uncoveredSkilledVisits.length > 0"
                type="warning"
                variant="tonal"
                icon="mdi-certificate-outline"
                class="mb-4"
              >
                {{ t('routePlan.skillCoverageWarning') }}
                <ul class="ml-4 mt-1">
                  <li
                    v-for="visit in uncoveredSkilledVisits"
                    :key="`${visit.patientId}-${visit.skillId}`"
                  >
                    {{ visit.patientName }} — {{ visit.skillName || t('routePlan.requiredSkill') }}
                  </li>
                </ul>
              </v-alert>

              <!-- Summary -->
              <div
                v-if="visitFields.length > 0"
                class="d-flex ga-2 mb-4 flex-wrap"
              >
                <v-chip
                  prepend-icon="mdi-calendar-check"
                  variant="tonal"
                >
                  {{ visitFields.length }} {{ t('routePlan.attentions') }}
                </v-chip>
                <v-chip
                  prepend-icon="mdi-account-multiple"
                  variant="tonal"
                >
                  {{ typedCaregiverIds.length }} {{ t('routePlan.caregivers').toLowerCase() }}
                </v-chip>
                <v-chip
                  v-if="fixedVisitsCount > 0"
                  prepend-icon="mdi-pin"
                  variant="tonal"
                  color="info"
                >
                  {{ fixedVisitsCount }} {{ t('routePlan.fixedTimeShort').toLowerCase() }}
                </v-chip>
              </div>

              <v-alert
                v-if="optimizeError"
                type="error"
                class="mb-4"
                closable
                @click:close="optimizeError = null"
              >
                {{ optimizeError }}
              </v-alert>

              <v-alert
                v-if="saveError"
                type="error"
                class="mb-4"
                closable
                @click:close="saveError = null"
              >
                {{ saveError }}
              </v-alert>

              <!-- Fixed-start tolerance override (per plan). Prefilled with the company
                   default; an unchanged value keeps inheriting the company setting. -->
              <v-divider class="mb-4" />
              <v-text-field
                v-model.number="fixedStartOffsetMinutes"
                v-bind="fixedStartOffsetMinutesAttrs"
                type="number"
                min="1"
                :max="MAX_FIXED_START_OFFSET_MINUTES"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-clock-plus-outline"
                suffix="min"
                class="fixed-start-offset"
                :label="t('routePlan.fixedStartTolerance')"
                :hint="t('routePlan.fixedStartToleranceHint', { minutes: defaultOffsetMinutes })"
                persistent-hint
                :error-messages="errors.fixedStartOffsetMinutes"
              />
            </v-card>
          </v-stepper-window-item>
        </v-stepper-window>

        <v-stepper-actions
          :class="currentStep === 0 ? 'justify-end' : 'justify-between'"
          :disabled="canGoToNextStep ? false : 'next'"
        >
          <template #prev>
            <v-btn
              v-if="currentStep !== 0"
              variant="outlined"
              @click="prev"
            >
              {{ t('shiftBatchCreateForm.previous') }}
            </v-btn>
          </template>
          <template #next>
            <div
              v-if="isLastStep"
              class="d-flex align-center ga-3"
            >
              <span
                v-if="lastSavedAt"
                class="text-caption text-medium-emphasis"
              >
                {{ t('routePlan.savedAt', { time: lastSavedAt }) }}
              </span>
              <v-btn
                variant="outlined"
                :loading="isSaving"
                :disabled="!meta.valid"
                @click="saveDraft()"
              >
                <v-icon start>
                  mdi-content-save-outline
                </v-icon>
                {{ t('routePlan.saveDraft') }}
              </v-btn>
              <v-btn
                color="primary"
                :loading="isOptimizing"
                :disabled="!canOptimize || !meta.valid"
                @click="onOptimize"
              >
                <v-icon start>
                  mdi-routes
                </v-icon>
                {{ t('routePlan.runOptimization') }}
              </v-btn>
            </div>
            <v-btn
              v-else
              color="primary"
              @click="validateStepAndAdvance(next)"
            >
              {{ t('shiftBatchCreateForm.next') }}
            </v-btn>
          </template>
        </v-stepper-actions>
      </template>
    </v-stepper>

    <route-plan-address-dialog
      v-model="addressDialog.open"
      :entity-type="addressDialog.entityType"
      :entity-id="addressDialog.entityId"
      :entity-name="addressDialog.entityName"
    />

    <route-plan-coverage-dialog
      v-model="coverageOpen"
      :route-plan="routePlan"
      readonly
    />

    <route-plan-optimize-overlay :active="isOptimizing" />
  </v-card>
</template>

<style scoped>
.fixed-start-offset {
  max-width: 280px;
}

.visit-duration {
  max-width: 120px;
  flex: none;
}

.visit-skill {
  min-width: 200px;
  max-width: 240px;
  flex: none;
}

.visit-time-input {
  max-width: 140px;
  flex: none;
}

.visit-caregiver {
  min-width: 220px;
  flex: 1;
}

.visit-address {
  min-width: 200px;
}

.setup-group-filter {
  max-width: 280px;
}

.caregiver-hours-name {
  flex: 0 0 220px;
  min-width: 0;
}
</style>
